"use client";

import { useTranslations } from "next-intl";
import { useState, type ReactNode } from "react";
import { LocaleSwitcher } from "@/components/LocaleSwitcher";
import { LayersTab, useHasLayers } from "@/components/Sidebar/LayersTab";
import { ToolsTab, useHasTools } from "@/components/Sidebar/ToolsTab";
import { FilterBar } from "@/features/filters/components/FilterBar";
import { useSelectedFilters } from "@/features/filters/useSelectedFilters";
import { logoUrl } from "@/features/media/mediaUrl";
import { useMediaContext } from "@/features/media/useMediaContext";
import { useProjectConfigs } from "@/features/project/ProjectContext";
import { Search } from "@/features/search/components/Search";
import type { AppLocale } from "@/i18n/routing";
import { cn } from "@/lib/cn";

/** Which slide-down panel is showing, or none. */
type Section = "search" | "filter" | "options" | null;

/**
 * The mobile menu: search, filters and options, reached from a small top-left bar.
 *
 * The legacy app's mobile layout had this and the new one had lost it — on a phone
 * there was no way to search, filter or toggle layers. It's rebuilt here in the
 * legacy shape (a row of icon buttons over a slide-down panel) but mounts the new
 * components (`Search`, `FilterBar`, `LayersTab`, `ToolsTab`) rather than the
 * legacy near-copies, so there's one implementation of each to maintain.
 *
 * A full-width top bar: the tenant logo on the left, the controls (and the
 * language switcher, the one bit that isn't a panel) on the right. Each button
 * toggles its panel; opening one closes the others, because they share the single
 * strip of screen below the bar. On mobile this bar replaces the desktop `ToolBar`.
 */
export function MobileMenu({ locales }: { locales: AppLocale[] }) {
  const t = useTranslations();
  const configs = useProjectConfigs();
  const { domain } = useMediaContext();
  const { selectedFilters } = useSelectedFilters();

  const hasLayers = useHasLayers();
  const hasTools = useHasTools();
  const hasOptions = hasLayers || hasTools;

  const [section, setSection] = useState<Section>(null);
  const toggle = (next: Exclude<Section, null>) =>
    setSection((current) => (current === next ? null : next));

  const logo = logoUrl(configs.appLogos?.[0]?.image?.fileName, domain);
  const logoLink = configs.appLogos?.[0]?.url;
  const filterCount = Object.keys(selectedFilters).length;

  return (
    <>
      <div
        className={cn(
          "pointer-events-auto fixed left-0 top-0 z-20 flex h-14 w-full items-center justify-between gap-1",
          "bg-white p-2 pr-3 dark:bg-dark-color shadow-md"
        )}
      >
        {logo && (
          <a
            href={logoLink}
            target="_blank"
            rel="noopener noreferrer"
            className="mr-1 flex shrink-0 items-center"
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img className="h-8 w-auto" src={logo} alt={t("logo")} />
          </a>
        )}

        <IconButton
          icon="ic-ios_Search1"
          label={t("mobile.search")}
          active={section === "search"}
          onClick={() => toggle("search")}
        />

        <IconButton
          icon="ic-jammaster_filter-f"
          label={t("mobile.filters")}
          active={section === "filter"}
          onClick={() => toggle("filter")}
        >
          {filterCount > 0 && section !== "filter" && (
            <span className="absolute -right-1 -top-1 min-w-[18px] rounded-full bg-primary px-1 text-center text-[10px] font-bold leading-4 dynamic-text">
              {filterCount}
            </span>
          )}
        </IconButton>

        {hasOptions && (
          <IconButton
            icon="ic-ios_Settings1"
            label={t("mobile.options")}
            active={section === "options"}
            onClick={() => toggle("options")}
          />
        )}

        <LocaleSwitcher locales={locales} />
      </div>

      {/* Kept mounted and toggled with `hidden` rather than swapped: each panel
          holds state (accepted search terms, open filter, scroll position) that a
          remount on every switch would throw away. */}
      <div
        aria-hidden={section === null}
        className={cn(
          "pointer-events-auto fixed left-0 top-14 z-40 w-full pb-4",
          "rounded-b-[25px] bg-white shadow-lg transition-all duration-300 ease-out dark:bg-dark-color",
          section ? "visible opacity-100" : "invisible opacity-0"
        )}
      >
        <div className={cn("p-4", section === "search" ? "block" : "hidden")}>
          <Search
            isOpen={section === "search"}
            setOpen={(open) => setSection(open ? "search" : null)}
          />
        </div>

        <div className={cn(section === "filter" ? "block" : "hidden")}>
          <FilterBar isCompact={false} />
        </div>

        {hasOptions && (
          <div className={section === "options" ? "block" : "hidden"}>
            {hasLayers && <LayersTab />}
            {hasTools && <ToolsTab />}
          </div>
        )}
      </div>
    </>
  );
}

/** A round icon button in the bar, matching the legacy 32px glyph buttons. */
function IconButton({
  icon,
  label,
  active,
  onClick,
  children,
}: {
  icon: string;
  label: string;
  active: boolean;
  onClick: () => void;
  children?: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      aria-expanded={active}
      className={cn(
        "relative flex h-8 w-8 items-center justify-center rounded-full",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        active && "bg-primary"
      )}
    >
      <i
        className={cn(
          "icon text-[16px]",
          icon,
          active
            ? "dynamic-text dark:text-black"
            : "text-black dark:text-off-white"
        )}
        aria-hidden
      />
      {children}
    </button>
  );
}
